/**
 * Shared presentational recommendation card — the single row layout used by BOTH
 * the Delivery `RecommendationsList` and the Discovery feature page, so the two
 * modes render identically (enhancement-discovery-rec-structured-title). Takes a
 * lean, mode-agnostic prop set (title + optional description + effort/impact + a
 * display `tag`); it does not know about Delivery vs Discovery dimension types.
 */

function effortBadgeClass(effort?: string): string {
  const map: Record<string, string> = {
    low: 'badge-success',
    medium: 'badge-warning',
    high: 'badge-neutral',
  };
  return effort ? (map[effort] ?? 'badge-info') : 'badge-info';
}

function impactBadgeClass(impact?: string): string {
  const map: Record<string, string> = {
    high: 'badge-success',
    medium: 'badge-warning',
    low: 'badge-neutral',
  };
  return impact ? (map[impact] ?? 'badge-info') : 'badge-info';
}

export interface RecommendationCardProps {
  /** 1-based rank shown in the number badge. */
  index: number;
  /** Bold heading — the opportunity-framed title. */
  title: string;
  /** Secondary paragraph. Omitted/empty renders no paragraph (legacy string recs). */
  description?: string;
  effort?: string;
  impact?: string;
  /** Right-aligned dimension badge text (already display-formatted). */
  tag: string;
}

export function RecommendationCard({
  index,
  title,
  description,
  effort,
  impact,
  tag,
}: RecommendationCardProps) {
  return (
    <div data-testid={`recommendation-${index - 1}`} className="rec-group-row">
      <div className="flex justify-between items-center mb-md">
        <div className="flex items-center gap-sm">
          <span
            className="text-xs font-bold"
            style={{
              backgroundColor: 'var(--genai-sem-accent)',
              color: 'var(--genai-sem-accent-fg)',
              borderRadius: 'var(--genai-radius-full)',
              width: '24px',
              height: '24px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {index}
          </span>
          <span className="text-base font-semibold text-primary">{title}</span>
        </div>
        <span className="badge badge-info">{tag}</span>
      </div>

      {description ? (
        <p className="text-base text-secondary" style={{ margin: '0 0 var(--genai-space-sm) 0' }}>
          {description}
        </p>
      ) : null}

      {(effort || impact) && (
        <div className="flex gap-sm">
          {effort && (
            <>
              <span className="text-xs text-muted">Effort:</span>
              <span className={`badge ${effortBadgeClass(effort)}`}>{effort}</span>
            </>
          )}
          {impact && (
            <>
              <span
                className="text-xs text-muted"
                style={{ marginLeft: 'var(--genai-space-sm)' }}
              >
                Impact:
              </span>
              <span className={`badge ${impactBadgeClass(impact)}`}>{impact}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
